import type { AssetCategory, AssetLane, Capacity, DailyAsset } from './dailyEngine'

export type PracticePreferenceState = 'active' | 'pinned' | 'archived' | 'quarantined'
export type WeeklyReviewState = 'draft' | 'submitted'
export type WeeklyQueueState = 'not_created' | 'held' | 'ready_for_coach' | 'run_started' | 'consumed' | 'superseded'
export type WeeklyPriority = 'self' | 'relationship' | 'fatherhood' | 'practical_load' | 'repair' | 'rest'
export type OpenLoopType = 'none' | 'promise' | 'rupture' | 'practical_load' | 'conversation' | 'other'
export type ValueAlignment = 'aligned' | 'mixed' | 'off_track' | 'prefer_not_to_rate'

export interface PracticeAssetStats {
  offered: number
  attempted: number
  completed: number
  helpful: number
  poorFit: number
  lastInteractionAt: string | null
}

export interface PracticeLibraryItem {
  asset: DailyAsset
  preference: PracticePreferenceState
  stats: PracticeAssetStats
}

export interface PracticeLibraryView {
  items: PracticeLibraryItem[]
  availability: 'ready' | 'waiting_for_coach'
  updatedAt: string
}

export interface PracticePreferenceRequest {
  assetId: string
  state: Exclude<PracticePreferenceState, 'quarantined'>
  clientMutationId: string
}

export interface WeeklyLaneCounts {
  self: number
  relationship: number
  fatherhood: number
}

export interface WeeklyFeedbackCounts {
  helpful: number
  neutral: number
  poor_fit: number
  too_much: number
  not_now: number
  unsafe: number
  already_doing: number
}

export interface WeeklyCouncilSummary {
  weekId: string
  startDate: string
  endDate: string
  activeDays: number
  meaningfulInteractions: number
  attempted: number
  completed: number
  skipped: number
  snoozed: number
  practicalActions: number
  reflectiveActions: number
  partnerQuestions: number
  laneCounts: WeeklyLaneCounts
  feedback: WeeklyFeedbackCounts
  openPracticeIds: string[]
}

export interface WeeklyCouncilDraft {
  weekId: string
  state: WeeklyReviewState
  revision: number
  capacityNextWeek: Capacity
  nextWeekPriority: WeeklyPriority
  valueAlignment: ValueAlignment
  openLoopType: OpenLoopType
  currentConstraint: string
  coachNote: string
  requestFreshPractices: boolean
  requestMoreTime: boolean
  privateReflectionPresent: boolean
  privateReflectionAiInclusion: 'excluded' | 'user_selected_pending_compaction'
  submittedAt?: string
  updatedAt: string
}

export interface WeeklyQueueProjection {
  queueId: string | null
  weekId: string
  state: WeeklyQueueState
  reasonCodes: string[]
  meaningfulInteractions: number
  submittedAt: string | null
  requestMoreTime?: boolean
}

export interface WeeklyCouncilView {
  summary: WeeklyCouncilSummary
  review: WeeklyCouncilDraft
  queue: WeeklyQueueProjection
}

export interface SaveWeeklyCouncilRequest {
  weekId: string
  expectedRevision: number
  state: WeeklyReviewState
  capacityNextWeek: Capacity
  nextWeekPriority: WeeklyPriority
  valueAlignment: ValueAlignment
  openLoopType: OpenLoopType
  currentConstraint: string
  coachNote: string
  requestFreshPractices: boolean
  requestMoreTime?: boolean
  privateReflection: string
  includePrivateReflectionInAi: boolean
  clientMutationId: string
}

export interface SaveWeeklyCouncilResult {
  view: WeeklyCouncilView
  reused: boolean
}

export interface WeeklyInteractionForSummary {
  assetId: string
  localDate: string
  lane: AssetLane
  category: AssetCategory
  action: 'draft' | 'completed' | 'skipped' | 'snoozed' | 'swapped'
  feedback?: keyof WeeklyFeedbackCounts
  asksPartnerQuestion?: boolean
}

export function mondayWeekId(localDate: string): string {
  const date = new Date(`${localDate}T12:00:00Z`)
  const day = date.getUTCDay()
  date.setUTCDate(date.getUTCDate() - (day === 0 ? 6 : day - 1))
  return date.toISOString().slice(0, 10)
}

export function emptyWeeklyCouncil(weekId: string, now = new Date().toISOString()): WeeklyCouncilDraft {
  return {
    weekId,
    state: 'draft',
    revision: 0,
    capacityNextWeek: 'normal',
    nextWeekPriority: 'self',
    valueAlignment: 'prefer_not_to_rate',
    openLoopType: 'none',
    currentConstraint: '',
    coachNote: '',
    requestFreshPractices: false,
    requestMoreTime: false,
    privateReflectionPresent: false,
    privateReflectionAiInclusion: 'excluded',
    updatedAt: now,
  }
}

export function compactWeeklySummary(weekId: string, interactions: WeeklyInteractionForSummary[]): WeeklyCouncilSummary {
  const start = new Date(`${weekId}T12:00:00Z`)
  const end = new Date(start)
  end.setUTCDate(end.getUTCDate() + 6)
  const laneCounts: WeeklyLaneCounts = { self: 0, relationship: 0, fatherhood: 0 }
  const feedback: WeeklyFeedbackCounts = { helpful: 0, neutral: 0, poor_fit: 0, too_much: 0, not_now: 0, unsafe: 0, already_doing: 0 }
  const activeDays = new Set<string>()
  const openPracticeIds = new Set<string>()
  let attempted = 0
  let completed = 0
  let skipped = 0
  let snoozed = 0
  let practicalActions = 0
  let reflectiveActions = 0
  let partnerQuestions = 0
  let meaningfulInteractions = 0

  for (const item of interactions) {
    activeDays.add(item.localDate)
    laneCounts[item.lane] += 1
    if (item.action === 'completed') completed += 1
    if (item.action === 'skipped') skipped += 1
    if (item.action === 'snoozed') snoozed += 1
    if (item.action !== 'draft' && item.action !== 'swapped') attempted += 1
    if (item.feedback) feedback[item.feedback] += 1
    if (item.action === 'completed' || item.feedback) meaningfulInteractions += 1
    if (item.category === 'shared_load' && item.action === 'completed') practicalActions += 1
    if (['reflection_values', 'regulation_presence'].includes(item.category) && item.action === 'completed') reflectiveActions += 1
    if (item.asksPartnerQuestion && item.action === 'completed') partnerQuestions += 1
    if (['snoozed', 'skipped'].includes(item.action) || item.feedback === 'not_now') openPracticeIds.add(item.assetId)
    if (item.action === 'completed') openPracticeIds.delete(item.assetId)
  }

  return {
    weekId,
    startDate: weekId,
    endDate: end.toISOString().slice(0, 10),
    activeDays: activeDays.size,
    meaningfulInteractions,
    attempted,
    completed,
    skipped,
    snoozed,
    practicalActions,
    reflectiveActions,
    partnerQuestions,
    laneCounts,
    feedback,
    openPracticeIds: [...openPracticeIds].slice(0, 20),
  }
}
